// API client pour les agences
export interface CreateAgencyData {
  name: string;
  address: string;
  city: string;
  wilaya?: string;
  contact_phone: string;
  contact_email: string;
  website_url?: string;
  brand_color?: string;
  subscription_plan: string;
  status?: 'active' | 'inactive' | 'suspended';
}

export interface UpdateAgencyData extends Partial<CreateAgencyData> {
  status?: 'active' | 'suspended';
  suspensionReason?: string;
  contact_phone?: string;
  contact_email?: string;
  subscription_plan?: string;
  logo_url?: string;
}

class AgencyAPI {
  private baseUrl = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'}/agencies`;

  async getAll() {
    const response = await fetch(this.baseUrl);
    if (!response.ok) throw new Error('Failed to fetch agencies');
    return response.json();
  }

  async getById(id: number) {
    const response = await fetch(`${this.baseUrl}/${id}`);
    if (!response.ok) throw new Error('Failed to fetch agency');
    return response.json();
  }

  async create(data: CreateAgencyData | FormData) {
    console.log('📤 AgencyAPI.create - Type de données:', data instanceof FormData ? 'FormData' : 'Object');

    const options: RequestInit = {
      method: 'POST',
    };

    // Si les données sont un FormData, ne pas définir Content-Type (le navigateur le fera automatiquement)
    if (data instanceof FormData) {
      console.log('📤 Envoi avec FormData');
      options.body = data;
    } else {
      console.log('📤 Envoi avec JSON');
      options.headers = { 'Content-Type': 'application/json' };
      options.body = JSON.stringify(data);
    }

    const response = await fetch(this.baseUrl, options);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to create agency' }));
      console.error('❌ Erreur création agence:', errorData);
      throw new Error(errorData.error || 'Failed to create agency');
    }

    const result = await response.json();
    console.log('✅ Agence créée avec succès:', result);
    return result;
  }

  async update(id: string, data: UpdateAgencyData | FormData) {
    console.log('🔄 AgencyAPI.update appelé pour ID:', id);
    console.log('🔄 Type de données:', data instanceof FormData ? 'FormData' : 'Object');

    const options: RequestInit = {
      method: 'PUT',
    };

    // Si les données sont un FormData, ne pas définir Content-Type
    if (data instanceof FormData) {
      console.log('📤 Mise à jour avec FormData');
      options.body = data;
    } else {
      console.log('📤 Mise à jour avec JSON');
      const bodyData = JSON.stringify(data);
      console.log('📤 Body JSON stringifié:', bodyData);
      options.headers = { 'Content-Type': 'application/json' };
      options.body = bodyData;
    }

    const response = await fetch(`${this.baseUrl}/${id}`, options);

    console.log('📡 Statut de réponse:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Réponse brute du serveur:', errorText);

      let error;
      try {
        error = JSON.parse(errorText);
      } catch (e) {
        error = { error: errorText };
      }

      console.error('❌ Erreur API parsée:', error);

      if (error.debug) {
        console.error('🐛 Infos de débogage du serveur:', error.debug);
      }

      throw new Error(error.error || 'Failed to update agency');
    }

    const result = await response.json();
    console.log('✅ Réponse API mise à jour agence:', result);
    return result;
  }

  async delete(id: string) {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to delete agency');
    return response.json();
  }

  async suspend(id: string, reason: string) {
    return this.update(id, { status: 'suspended', suspensionReason: reason });
  }

  async reactivate(id: string) {
    return this.update(id, { status: 'active', suspensionReason: undefined });
  }
}

export const agencyAPI = new AgencyAPI();